import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import LoadingIndicator from "@/components/ui/loading-indicator";
import { user } from "@/services/user/user-service";
import { Icon } from "@iconify/react";

export default function EmailUnsubscribePage() {
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);

  const handleUnsubscribe = async () => {
    try {
      setLoading(true);
      await user.changeEmailPreference("unsubscribe");
      setSuccess(true);
      setIsSubscribed(false);
    } catch (error: any) {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleResubscribe = async () => {
    try {
      setLoading(true);
      await user.changeEmailPreference("subscribe");
      setSuccess(true);
      setIsSubscribed(true);
    } catch (error: any) {
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    handleUnsubscribe();
  }, []);

  if (loading) {
    return <LoadingIndicator label="Processing unsubscribe request" />;
  }

  return (
    <div className="container mx-auto mt-8 max-w-2xl mb-10">
      <Card className="p-6 bg-lime-50">
        <div className="text-center py-8">
          {success && !isSubscribed && (
            <>
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                  <Icon icon="heroicons:check" className="w-8 h-8 text-green-600" />
                </div>
              </div>
              <h1 className="text-xl font-semibold text-green-600 mb-2">
                Successfully Unsubscribed
              </h1>
              <p className="text-gray-600 mb-6">
                You have been successfully unsubscribed from our email communications.
                You will no longer receive promotional emails from Cobrief.
              </p>
              <p className="text-gray-500 text-sm mb-6">
                If you change your mind, you can always resubscribe below.
              </p>
              <Button
                onClick={handleResubscribe}
                variant="outline"
                className="flex gap-2 items-center mx-auto mb-4"
                disabled={loading}
              >
                <Icon icon="heroicons:envelope" className="w-4 h-4" />
                {loading ? "Processing..." : "Resubscribe to Emails"}
              </Button>
            </>
          )}

          {success && isSubscribed && (
            <>
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                  <Icon icon="heroicons:envelope" className="w-8 h-8 text-blue-600" />
                </div>
              </div>
              <h1 className="text-xl font-semibold text-blue-600 mb-2">
                Successfully Resubscribed
              </h1>
              <p className="text-gray-600 mb-6">
                You have been successfully resubscribed to our email communications.
                You will now receive updates and promotional emails from Cobrief.
              </p>
              <p className="text-gray-500 text-sm mb-6">
                You can unsubscribe again at any time from the emails.
              </p>
            </>
          )}

          {error && (
            <>
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                  <Icon icon="heroicons:exclamation-triangle" className="w-8 h-8 text-red-600" />
                </div>
              </div>
              <h1 className="text-xl font-semibold text-red-600 mb-2">
                Unsubscribe Failed
              </h1>
              <p className="text-gray-600 mb-6">
                We encountered an error while processing your unsubscribe request.
                Please try again later or contact our support team.
              </p>
            </>
          )}

          <a href="/">
            <Button
              variant="accentGreen"
              className="flex gap-2 items-center mx-auto"
            >
              <Icon icon="heroicons:home" className="w-4 h-4" />
              Go to Homepage
            </Button>
          </a>
        </div>
      </Card>
    </div>
  );
}
